import React from "react";
import { StatusPill, getTypeIcon } from "./MessagesCommon";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Box } from "@mui/material";

export default function MessageDetail({ msg, onClose }) {
  const baseTargets = msg.targets ?? [];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
    }}
    onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#fff",
        borderRadius: "16px",
        width: "100%",
        maxWidth: "640px",
        maxHeight: "85vh",
        overflow: "auto",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
      }}>
        {/* Header Section */}
        <div style={{ padding: "24px 28px", borderBottom: "1px solid #f0f0f4", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
              <span style={{ fontSize: "26px" }}>{getTypeIcon(msg.messageType)}</span>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1a202c" }}>
                {msg.subject || <em style={{ color: "#a0aec0", fontWeight: 400 }}>(no subject)</em>}
              </h2>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <StatusPill status={msg.status} direction="OUTBOX" />
              {msg.messageType && (
                <span style={{ fontSize: "11px", color: "#718096", background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: "4px", padding: "2px 8px" }}>
                  {msg.messageType}
                </span>
              )}
              <span style={{ fontSize: "11px", color: "#2b6cb0", background: "#ebf8ff", border: "1px solid #bee3f8", borderRadius: "4px", padding: "2px 8px" }}>
                Scope: {msg.recipientScope}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#a0aec0", padding: "0", marginLeft: "16px" }}>✕</button>
        </div>

        {/* Metadata Grid */}
        <div style={{ padding: "20px 28px", borderBottom: "1px solid #f0f0f4", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <MetaItem label="Broadcast ID" value={msg.id} />
          <MetaItem label="Dispatched By" value={msg.sentBy || "System Admin"} />
          <MetaItem label="Sent Timestamp" value={msg.createdAt ? new Date(msg.createdAt).toLocaleString() : "—"} />
          <MetaItem label="Delivery Rate" value={`${msg.delivered} / ${msg.targetCount} Connectors`} />
        </div>

        {/* Message Content Body */}
        <div style={{ padding: "20px 28px", borderBottom: baseTargets.length > 0 ? "1px solid #f0f0f4" : "none" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#718096", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Message Content</div>
          <div style={{ fontSize: "15px", color: "#2d3748", lineHeight: 1.7, whiteSpace: "pre-wrap", background: "#f7fafc", padding: "14px", borderRadius: "8px", border: "1px solid #edf2f7" }}>
            {msg.body}
          </div>

          {msg.error && (
            <div style={{ marginTop: "12px", background: "#fff5f5", border: "1px solid #fc8181", borderRadius: "8px", padding: "12px 16px", fontSize: "13px", color: "#c53030" }}>
              <strong>Global Error:</strong> {msg.error}
            </div>
          )}
        </div>

        {/* Dynamic Individual Delivery Targets Tracker Checklist */}
        {baseTargets.length > 0 && (
          <div style={{ padding: "20px 28px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#718096", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Recipient Status Checklist</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {baseTargets.map((target, index) => (
                <div key={index} style={{
                  padding: "10px 14px",
                  background: target.delivered ? "#f0fff4" : "#fff5f5",
                  border: `1px solid ${target.delivered ? "#c6f6d5" : "#fed7d7"}`,
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "13px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: target.delivered ? "#22543d" : "#822727",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {target.delivered ? (
                        <CheckCircleOutlineOutlinedIcon fontSize="small" />
                      ) : (
                        <CancelOutlinedIcon fontSize="small" />
                      )}
                      {target.connector}
                    </Box>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#718096" }}>
                      HTTP {target.httpStatus || "0"}
                    </span>
                  </div>
                  {target.url && (
                    <span style={{ fontSize: "11px", color: "#4a5568", marginTop: "2px" }}>
                      {target.url}
                    </span>
                  )}
                  {target.error && (
                    <span style={{ fontSize: "11px", color: "#c53030", marginTop: "4px", background: "rgba(255,255,255,0.6)", padding: "4px 8px", borderRadius: "4px" }}>
                      <strong>Error Details:</strong> {target.error}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span style={{ fontSize: "11px", color: "#a0aec0", textTransform: "uppercase", fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: "13px", color: "#2d3748", fontWeight: 500, wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}
